import { resolveVariant, getAvailableValues, currencySymbol, fetchFullProduct } from './variantUtils.js';

export class VariationCarouselPicker {
  constructor(options = {}) {
    this.onProductAdd = options.onProductAdd || (() => {});
    this.onClose = options.onClose || (() => {});
    this.primaryColor = options.primaryColor || '#3b82f6';
    this.container = null;
    this.product = null;
    this.selectedValues = []; // one entry per option dimension
  }

  // ── Public API ──────────────────────────────────────────

  show(product) {
    this.hide();
    this.product = product;

    // If variants not loaded yet, show loading state and fetch
    if (!product.variants) {
      this._injectStyles();
      this._buildLoadingDOM();
      this._positionAboveMobileBar();
      document.body.appendChild(this.container);

      fetchFullProduct(product).then(fullProduct => {
        if (!fullProduct || !fullProduct.variants) {
          this.hide();
          return;
        }
        // Re-show with full data — skip onClose so carousel stays hidden during transition
        this.hide(true);
        this.show(fullProduct);
      });
      return;
    }

    // Normal flow — variants already loaded
    this.selectedValues = new Array(product.options.length).fill(null);
    product.options.forEach((opt, i) => {
      const available = getAvailableValues(product.variants, i, this.selectedValues);
      if (available.length > 0) this.selectedValues[i] = available[0];
    });

    this._injectStyles();
    this._buildDOM();
    this._positionAboveMobileBar();
    document.body.appendChild(this.container);
  }

  /**
   * @param {boolean} [skipOnClose] - When true, do not invoke onClose. Use when replacing with a new picker (e.g. loading → full).
   */
  hide(skipOnClose = false) {
    if (this.container && this.container.parentNode) {
      this.container.classList.add('ttp-varpicker--hiding');
      const c = this.container;
      const onClose = this.onClose;
      setTimeout(() => {
        if (c.parentNode) c.parentNode.removeChild(c);
        if (!skipOnClose) onClose();
      }, 280);
    }
    this.container = null;
    this.product = null;
  }

  destroy() {
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
    this.container = null;
    const style = document.getElementById('ttp-varpicker-styles');
    if (style) style.remove();
  }

  // ── DOM construction ────────────────────────────────────

  _buildLoadingDOM() {
    this.container = document.createElement('div');
    this.container.className = 'ttp-varpicker';
    this.container.style.setProperty('--ttp-vp-primary', this.primaryColor);
    this.container.innerHTML = `
      <div class="ttp-vp-backdrop"></div>
      <div class="ttp-vp-card" style="align-items:center;justify-content:center;min-height:120px;">
        <div style="display:flex;flex-direction:column;align-items:center;gap:10px;padding:24px;">
          <div class="ttp-vp-spinner"></div>
          <div style="font-size:12px;color:#64748b;font-weight:500;">Loading options...</div>
        </div>
      </div>
    `;
    this.container.querySelector('.ttp-vp-backdrop').addEventListener('click', () => {
      this.hide();
    });
  }

  _buildDOM() {
    const p = this.product;
    const sym = currencySymbol(p.currency);
    const resolvedVariant = resolveVariant(p.variants, this.selectedValues);
    const currentPrice = resolvedVariant ? resolvedVariant.price : p.price;

    this.container = document.createElement('div');
    this.container.className = 'ttp-varpicker';
    this.container.style.setProperty('--ttp-vp-primary', this.primaryColor);

    this.container.innerHTML = `
      <div class="ttp-vp-backdrop"></div>
      <div class="ttp-vp-card" id="ttpVpCard">

        <!-- Collapsed: product summary (left panel) -->
        <div class="ttp-vp-left" id="ttpVpLeft">
          <div class="ttp-vp-img" id="ttpVpImg">${p.imageUrl
            ? `<img src="${p.imageUrl}" alt="${p.name}" onerror="this.style.display='none'"/>`
            : ''}</div>
          <div class="ttp-vp-product-name">${p.name}</div>
          <div class="ttp-vp-product-price" id="ttpVpPrice">${sym}${currentPrice.toFixed(2)}</div>
        </div>

        <!-- Options panel (right panel) -->
        <div class="ttp-vp-right" id="ttpVpRight">
          <div class="ttp-vp-right-header">
            <span class="ttp-vp-right-title">Choose Options</span>
            <button class="ttp-vp-close-btn" id="ttpVpClose">✕</button>
          </div>
          <div class="ttp-vp-options" id="ttpVpOptions">
            ${this._renderOptions()}
          </div>
          <button class="ttp-vp-add-btn" id="ttpVpAddBtn">
            Add to Cart — ${sym}${currentPrice.toFixed(2)}
          </button>
        </div>
      </div>
    `;

    this.container.querySelector('#ttpVpClose').addEventListener('click', () => {
      this.hide();
    });

    this.container.querySelector('#ttpVpAddBtn').addEventListener('click', () => {
      this._handleAdd();
    });

    this._attachOptionHandlers();
    this._updateAddButton();
  }

  _renderOptions() {
    return this.product.options.map((opt, optIdx) => {
      const availableValues = getAvailableValues(
        this.product.variants, optIdx, this.selectedValues
      );
      const isColor = opt.name.toLowerCase() === 'color' ||
                      opt.name.toLowerCase() === 'colour';

      if (isColor) {
        return `
          <div class="ttp-vp-section">
            <div class="ttp-vp-label">${opt.name}</div>
            <div class="ttp-vp-swatches" data-opt-idx="${optIdx}">
              ${opt.values.map(val => {
                const oos = !availableValues.includes(val);
                const selected = this.selectedValues[optIdx] === val;
                return `<div
                  class="ttp-vp-swatch${selected ? ' selected' : ''}${oos ? ' oos' : ''}"
                  data-value="${val}"
                  data-opt-idx="${optIdx}"
                  style="background:${this._colorNameToHex(val)};"
                  title="${val}${oos ? ' (unavailable)' : ''}"
                ></div>`;
              }).join('')}
            </div>
          </div>`;
      }

      return `
        <div class="ttp-vp-section">
          <div class="ttp-vp-label">${opt.name}</div>
          <div class="ttp-vp-pills" data-opt-idx="${optIdx}">
            ${opt.values.map(val => {
              const oos = !availableValues.includes(val);
              const selected = this.selectedValues[optIdx] === val;
              return `<button
                class="ttp-vp-pill${selected ? ' selected' : ''}${oos ? ' oos' : ''}"
                data-value="${val}"
                data-opt-idx="${optIdx}"
                ${oos ? 'disabled' : ''}
              >${val}</button>`;
            }).join('')}
          </div>
        </div>`;
    }).join('');
  }

  _attachOptionHandlers() {
    this.container.querySelectorAll('.ttp-vp-swatch:not(.oos)').forEach(el => {
      el.addEventListener('click', () => this._selectOption(
        parseInt(el.dataset.optIdx), el.dataset.value
      ));
    });
    this.container.querySelectorAll('.ttp-vp-pill:not(.oos)').forEach(el => {
      el.addEventListener('click', () => this._selectOption(
        parseInt(el.dataset.optIdx), el.dataset.value
      ));
    });
  }

  _selectOption(optIdx, value) {
    this.selectedValues[optIdx] = value;
    // Re-render options (availability may change for other dimensions)
    this.container.querySelector('#ttpVpOptions').innerHTML = this._renderOptions();
    this._attachOptionHandlers();
    this._updateAddButton();
    this._updateImage();
  }

  _updateAddButton() {
    const sym = currencySymbol(this.product.currency);
    const variant = resolveVariant(this.product.variants, this.selectedValues);
    const btn = this.container.querySelector('#ttpVpAddBtn');
    const allSelected = this.selectedValues.every(v => v !== null);

    if (!allSelected) {
      btn.textContent = 'Select all options';
      btn.disabled = true;
      return;
    }
    if (!variant || !variant.available) {
      btn.textContent = 'Unavailable';
      btn.disabled = true;
      return;
    }
    const price = variant.price ?? this.product.price;
    btn.textContent = `Add to Cart — ${sym}${price.toFixed(2)}`;
    btn.disabled = false;

    // Update price display in left panel
    const priceEl = this.container.querySelector('#ttpVpPrice');
    if (priceEl) priceEl.textContent = `${sym}${price.toFixed(2)}`;
  }

  _updateImage() {
    const variant = resolveVariant(this.product.variants, this.selectedValues);
    const imgUrl = variant?.imageUrl || this.product.imageUrl;
    const imgEl = this.container.querySelector('#ttpVpImg');
    if (!imgEl) return;
    if (imgUrl) {
      imgEl.innerHTML = `<img src="${imgUrl}" alt="${this.product.name}"
        onerror="this.style.display='none'"/>`;
    }
  }

  _handleAdd() {
    const variant = resolveVariant(this.product.variants, this.selectedValues);
    if (!variant || !variant.available) return;

    const btn = this.container.querySelector('#ttpVpAddBtn');
    btn.textContent = '✓ Added!';
    btn.disabled = true;

    const productData = {
      ...this.product,
      id: variant.id,           // ← override with specific variant id
      price: variant.price,
      selectedOptions: this.selectedValues,
      selectedAmount: 1,
      sellBy: 'quantity',
    };

    this.onProductAdd(this.container, productData);
    setTimeout(() => this.hide(), 800);
  }

  // ── Positioning ─────────────────────────────────────────

  _positionAboveMobileBar() {
    /* Full viewport overlay - no bottom inset so backdrop fully obscures background */
  }

  // ── Color name → hex (best effort, falls back to grey) ──

  _colorNameToHex(name) {
    const map = {
      black: '#1e293b', white: '#f8fafc', red: '#ef4444',
      blue: '#3b82f6', green: '#22c55e', yellow: '#eab308',
      orange: '#f97316', purple: '#a855f7', pink: '#ec4899',
      grey: '#94a3b8', gray: '#94a3b8', navy: '#1e3a5f',
      brown: '#92400e', beige: '#d4b483', cream: '#fef9c3',
    };
    return map[name.toLowerCase()] || '#94a3b8';
  }

  // ── Styles ──────────────────────────────────────────────

  _injectStyles() {
    if (document.getElementById('ttp-varpicker-styles')) return;
    const s = document.createElement('style');
    s.id = 'ttp-varpicker-styles';
    s.textContent = `

.ttp-varpicker {
  position: fixed !important;
  inset: 0;
  z-index: 2147483647 !important;
  display: flex !important;
  align-items: center;
  justify-content: center;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  animation: ttpVpIn 0.3s cubic-bezier(0.23,1,0.32,1);
  padding: 16px;
}

.ttp-varpicker--hiding {
  animation: ttpVpOut 0.28s ease forwards;
  pointer-events: none;
}

@keyframes ttpVpIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}
@keyframes ttpVpOut {
  from { opacity: 1; }
  to   { opacity: 0; }
}

.ttp-vp-backdrop {
  position: absolute;
  inset: 0;
  background: #0a0a0a;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
}

.ttp-vp-card {
  position: relative !important;
  z-index: 1 !important;
  width: 310px;
  background: #fff;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 16px 56px rgba(0,0,0,0.22);
  display: flex !important;
  flex-direction: row;
  animation: ttpVpCardIn 0.35s cubic-bezier(0.23,1,0.32,1);
}

@keyframes ttpVpCardIn {
  from { transform: translateY(20px) scale(0.96); opacity: 0; }
  to   { transform: translateY(0) scale(1); opacity: 1; }
}

/* Left panel — product image + name + price */
.ttp-vp-left {
  width: 110px;
  flex-shrink: 0;
  background: linear-gradient(160deg, #f0f4ff, #dde6f8);
  display: flex !important;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 14px 8px;
  border-right: 1px solid #f1f5f9;
}

.ttp-vp-img {
  width: 72px !important; height: 72px !important;
  border-radius: 14px;
  background: #fff;
  border: 1px solid #e5e7eb;
  display: flex !important; align-items: center; justify-content: center;
  overflow: hidden;
  transition: all 0.2s ease;
}

.ttp-vp-img img {
  width: 100%; height: 100%;
  object-fit: contain;
  padding: 6px;
}

.ttp-vp-product-name {
  font-size: 11px;
  font-weight: 700;
  color: #1e293b;
  text-align: center;
  line-height: 1.25;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.ttp-vp-product-price {
  font-size: 14px;
  font-weight: 700;
  color: var(--ttp-vp-primary, #3b82f6);
}

/* Right panel — options */
.ttp-vp-right {
  flex: 1;
  display: flex !important;
  flex-direction: column;
  padding: 12px 12px 10px;
  gap: 8px;
  min-width: 0;
}

.ttp-vp-right-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2px;
}

.ttp-vp-right-title {
  font-size: 11px;
  font-weight: 700;
  color: #1e293b;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.ttp-vp-close-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  font-size: 13px;
  padding: 2px 4px;
  border-radius: 4px;
  line-height: 1;
}
.ttp-vp-close-btn:hover { background: #f1f5f9; color: #475569; }

.ttp-vp-options {
  flex: 1;
  display: flex !important;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
}

.ttp-vp-section { display: flex !important; flex-direction: column; gap: 5px; }

.ttp-vp-label {
  font-size: 9px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Color swatches */
.ttp-vp-swatches { display: flex !important; flex-wrap: wrap; gap: 6px; }

.ttp-vp-swatch {
  display: block !important;
  width: 22px !important; height: 22px !important;
  border-radius: 50%;
  cursor: pointer;
  border: 2.5px solid transparent;
  transition: all 0.15s;
  flex-shrink: 0;
  position: relative;
}
.ttp-vp-swatch:hover { transform: scale(1.12); }
.ttp-vp-swatch.selected {
  border-color: var(--ttp-vp-primary, #3b82f6);
  box-shadow: 0 0 0 2px #fff, 0 0 0 3.5px var(--ttp-vp-primary, #3b82f6);
}
.ttp-vp-swatch.oos { opacity: 0.3; cursor: not-allowed; }

/* Size pills */
.ttp-vp-pills { display: flex !important; flex-wrap: wrap; gap: 4px; }

.ttp-vp-pill {
  padding: 3px 9px;
  border-radius: 6px;
  border: 1.5px solid #e2e8f0;
  background: #fff;
  font-size: 10px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;
  line-height: 1.4;
}
.ttp-vp-pill:hover:not(:disabled) { border-color: var(--ttp-vp-primary, #3b82f6); color: var(--ttp-vp-primary, #3b82f6); }
.ttp-vp-pill.selected { border-color: var(--ttp-vp-primary, #3b82f6); background: var(--ttp-vp-primary, #3b82f6); color: #fff; }
.ttp-vp-pill.oos { opacity: 0.35; cursor: not-allowed; text-decoration: line-through; }

/* Add button */
.ttp-vp-add-btn {
  width: 100%;
  padding: 9px;
  background: linear-gradient(135deg, var(--ttp-vp-primary, #3b82f6), #2563eb);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  box-shadow: 0 3px 10px rgba(59,130,246,0.3);
  transition: all 0.15s;
  flex-shrink: 0;
}
.ttp-vp-add-btn:hover:not(:disabled) { transform: scale(0.98); }
.ttp-vp-add-btn:disabled { background: #cbd5e1; box-shadow: none; cursor: not-allowed; }

.ttp-vp-spinner {
  width: 24px; height: 24px;
  border: 3px solid #e2e8f0;
  border-top-color: var(--ttp-vp-primary, #3b82f6);
  border-radius: 50%;
  animation: ttpVpSpin 0.7s linear infinite;
}
@keyframes ttpVpSpin {
  to { transform: rotate(360deg); }
}

    `;
    document.head.appendChild(s);
  }
}
